import axios from "axios";
import axiosInstance from "../lib/axios.instance";
import type {
  IDocument,
  ICreateDocumentRequest,
  ISaveDocumentContent,
} from "../types/document.types";

export interface ICreateDocumentResponse {
  document_id: string;
}

const BASE_URL = "/documents";

// Normalise trailing slash so `${SFDT_BASE}documenteditor/…` is always correct
// regardless of whether WORDPROCESSOR_API_URL was set with or without a trailing slash.
const SFDT_BASE = (process.env.WORDPROCESSOR_API_URL ?? "").replace(/\/?$/, "/");

export const getDocument = async (docID: string): Promise<IDocument> => {
  const response = await axiosInstance.get<IDocument>(`${BASE_URL}/${docID}`);
  return response.data;
};

export const createDocument = async (
  payload: ICreateDocumentRequest
): Promise<ICreateDocumentResponse> => {
  try {
    const response = await axiosInstance.post<ICreateDocumentResponse>(`${BASE_URL}/`, payload);
    return response.data;
  } catch (error) {
    throw new Error(
      error instanceof Error && error.message !== "" ? error.message : "Failed to create document"
    );
  }
};

export const saveDocumentContent = async (data: ISaveDocumentContent): Promise<void> => {
  await axiosInstance.put(`${BASE_URL}/${data.id}`, {
    content: data.content,
  });
};

/**
 * Convert a .docx Blob to SFDT via the Syncfusion Import endpoint
 */
export const convertDocxToSfdt = async (docxBlob: Blob): Promise<string> => {
  try {
    const formData = new FormData();
    formData.append("files", docxBlob, "document.docx");

    // Do NOT set Content-Type manually for FormData: the browser must generate
    // the boundary token (e.g. multipart/form-data; boundary=----Webkit…).
    // Overriding it strips the boundary and causes the server to reject the body.
    const response = await axios.post(
      `${SFDT_BASE}documenteditor/Import`,
      formData,
      { timeout: 360_000 } // 6-minute timeout
    );
    // Syncfusion returns a JSON SFDT object; stringify it for storage/transport.
    return JSON.stringify(response.data);
  } catch (error) {
    throw new Error(
      error instanceof Error && error.message !== ""
        ? error.message
        : "Failed to convert DOCX to SFDT"
    );
  }
};

/**
 * Convert HTML to SFDT
 */
export const convertHtmlToSfdt = async (htmlContent: string): Promise<string> => {
  try {
    const response = await axios.post(
      `${SFDT_BASE}documenteditor/SystemClipboard`,
      {
        content: htmlContent,
        type: ".html",
      },
      { timeout: 360_000 } // 6-minute timeout
    );
    // Syncfusion returns a JSON SFDT object; stringify it for storage/transport.
    return JSON.stringify(response.data);
  } catch (error) {
    throw new Error(
      error instanceof Error && error.message !== ""
        ? error.message
        : "Failed to convert HTML to SFDT"
    );
  }
};
